import React from 'react';
import PropTypes from "prop-types";
import modifyDate from "date-fns/set";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import isSameDay from "date-fns/isSameDay";
import formatDate from "date-fns/format";
import { default as RDatePicker, registerLocale } from 'react-datepicker';
import cn from "classnames";
import { Button } from "~/native-ui/Button";
import { Box, HStack, VStack } from "~/native-ui/native-base";
import { useThemeColors } from '../hooks';
import getConfig from "~/config";
import de from "date-fns/locale/de";
import en from "date-fns/locale/en-GB";
import fr from "date-fns/locale/fr";

let locale = getConfig().translations?.locale;
const getLocale = () => {
    var locales = { en, de, fr };
    
    if (!locales[locale]) {
        locale = "de"
    }
    
    return locales[locale];
}

registerLocale(locale, getLocale());

const DatePickerContainer = React.memo((props) => {
    const { className, children, } = props;
    const calendarClassName = React.useRef(`calendar-${new Date().getTime()}`).current;
    const { primaryColor, primaryContrast, primaryHover, primaryColorDark } = useThemeColors();
    
    const pickerStyle = `
        .${calendarClassName} {
            width: 300px;
        }

        .${calendarClassName} .react-datepicker__year--container,
        .${calendarClassName} .react-datepicker__month-container {
            float: none;
        }

        .${calendarClassName} .react-datepicker__year-wrapper,
        .${calendarClassName} .react-datepicker__month-wrapper,
        .${calendarClassName} .react-datepicker__month {
            max-width: none;
            display: flex;
            justify-content: flex-start;
            column-gap: calc(4% / 4);
            row-gap: 5px;
        }

        .${calendarClassName} .react-datepicker__month {
            flex-direction: column;
        }

        .${calendarClassName} .react-datepicker__day-name,
        .${calendarClassName} .react-datepicker__day,
        .${calendarClassName} .react-datepicker__time-name{
            width: calc(100% / 8);
        }

        .${calendarClassName} .react-datepicker__year-text,
        .${calendarClassName} .react-datepicker__month-text {
            width: 0%;
            flex: 1 1 var(--item-flex-basis);
            max-width: var(--item-flex-basis);
            box-sizing: border-box;
            padding: 5px;
            margin: 0;
        }

        .${calendarClassName} .react-datepicker__year-text {
            --item-flex-basis: 19%;
        }

        .${calendarClassName} .react-datepicker__month-text {
            --item-flex-basis: 31%;
        }

        .${calendarClassName} .react-datepicker__header--custom {
            padding: 0;
        }

        .${calendarClassName} .react-datepicker__day--keyboard-selected {
            background: none;
            color: inherit;
        }

        .${calendarClassName} .react-datepicker__day:hover,
        .${calendarClassName} .react-datepicker__month-text:hover,
        .${calendarClassName} .react-datepicker__quarter-text:hover,
        .${calendarClassName} .react-datepicker__year-text:hover {
            background: ${primaryHover};
            color: ${primaryColor};
        }

        .${calendarClassName} .react-datepicker__day--selected,
        .${calendarClassName} .react-datepicker__day--in-selecting-range,
        .${calendarClassName} .react-datepicker__day--in-range,
        .${calendarClassName} .react-datepicker__month-text--selected,
        .${calendarClassName} .react-datepicker__month-text--in-selecting-range,
        .${calendarClassName} .react-datepicker__month-text--in-range,
        .${calendarClassName} .react-datepicker__quarter-text--selected,
        .${calendarClassName} .react-datepicker__quarter-text--in-selecting-range,
        .${calendarClassName} .react-datepicker__quarter-text--in-range,
        .${calendarClassName} .react-datepicker__year-text--selected,
        .${calendarClassName} .react-datepicker__year-text--in-selecting-range,
        .${calendarClassName} .react-datepicker__year-text--in-range,
        .${calendarClassName} .react-datepicker__month-text--keyboard-selected,
        .${calendarClassName} .react-datepicker__quarter-text--keyboard-selected,
        .${calendarClassName} .react-datepicker__year-text--keyboard-selected {
            background: ${primaryColor};
            color: ${primaryContrast};
        }

        .${calendarClassName} .react-datepicker__day--selected:hover,
        .${calendarClassName} .react-datepicker__day--in-selecting-range:hover,
        .${calendarClassName} .react-datepicker__day--in-range:hover,
        .${calendarClassName} .react-datepicker__month-text--selected:hover,
        .${calendarClassName} .react-datepicker__month-text--in-selecting-range:hover,
        .${calendarClassName} .react-datepicker__month-text--in-range:hover,
        .${calendarClassName} .react-datepicker__quarter-text--selected:hover,
        .${calendarClassName} .react-datepicker__quarter-text--in-selecting-range:hover,
        .${calendarClassName} .react-datepicker__quarter-text--in-range:hover,
        .${calendarClassName} .react-datepicker__year-text--selected:hover,
        .${calendarClassName} .react-datepicker__year-text--in-selecting-range:hover,
        .${calendarClassName} .react-datepicker__year-text--in-range:hover,
        .${calendarClassName} .react-datepicker__month-text--keyboard-selected:hover,
        .${calendarClassName} .react-datepicker__quarter-text--keyboard-selected:hover,
        .${calendarClassName} .react-datepicker__year-text--keyboard-selected:hover  {
            background: ${primaryColorDark};
            color: ${primaryContrast};
        }

        .${calendarClassName} .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            row-gap: 5px;
        }
        
        
        .${calendarClassName} .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item {
            flex: 1 0 calc(100% / 6.5);
            box-sizing: border-box;
            max-width: calc(100% / 6.5);
            padding: 0;
            height: auto;
            padding: 6px;
            border-radius: .3em;
        }

        .${calendarClassName} .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item {
            background: none;
            color: inherit;
        }

        .${calendarClassName} .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item:hover {
            background: ${primaryHover};
            color: ${primaryColor};
        }

        .${calendarClassName} .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected {
            background: ${primaryColor};
            color: ${primaryContrast};
        }

        .${calendarClassName} .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected:hover {
            background: ${primaryColorDark};
            color: ${primaryContrast};
        }

        .${calendarClassName} .react-datepicker__time-container {
            width: 100%;
        }
        
        .${calendarClassName} .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box {
            width: 100%;
            padding: 5px;
            box-sizing: border-box;
        }

        .${calendarClassName} .react-datepicker__header--time {
            padding: 8px;
        }

        .${calendarClassName} .react-datepicker-time__header {
            font-size: 14px;
            font-weight: normal;
        }

        @media (min-width: 500px){
            .${calendarClassName}.react-datepicker--time-only {
                width: 450px;
            }
        }
    `

    return (
        <div className={ cn(calendarClassName, className) }>
            <VStack fontSize="xs">
                { children }
            </VStack>
            <style>{ pickerStyle }</style>
        </div>
    )
});


const CalendarCustomHeader = React.memo((props) => {
    const {
        date,
        showingMonths, showingYears,
        showMonthView, showYearView,
        decreaseYear, increaseYear,
        decreaseMonth, increaseMonth,
        prevMonthButtonDisabled, nextMonthButtonDisabled,
    } = props;

    const [ decrease, increase, ] = showingYears ? [ decreaseYear, increaseYear, ] : [ decreaseMonth, increaseMonth, ]
    const locale = getLocale();
    
    return (
        <VStack p="2" space="1">
            <Box>{ formatDate(date, "dd MMMM yyyy", { locale }) }</Box>
            <HStack width="100%" space="2">
                <Button flex={0} px="4" icon="keyboard-arrow-left" onPress={decrease} disabled={prevMonthButtonDisabled} />
                <HStack flex={1} justifyContent="center" space="2">
                    <Button primary px="4" ghost={showingYears} onPress={showYearView} label={formatDate(date, "yyyy", { locale })} />
                    <Button primary px="4" ghost={showingMonths} onPress={showMonthView} label={formatDate(date, "MMMM", { locale })} />
                </HStack>
                <Button flex={0} px="4" icon="keyboard-arrow-right" onPress={increase} disabled={nextMonthButtonDisabled} />
            </HStack>
        </VStack>
    )
});



export class DateTimePickerComponent extends React.Component {
    static propTypes = {
        mode: PropTypes.oneOf(["date", "time", "datetime"]).isRequired,
        startView: PropTypes.oneOf(["years", "months", "dates"]).isRequired,
        onChange: PropTypes.func.isRequired,
        isInline: PropTypes.bool,
    }

    static defaultProps = {
        startView: "dates",
    }

    
    isTime = this.props.display === "clock"
    

    state = {
        startView: this.props.startView,
        showTimeSelect: this.isTime,
        showTimeSelectOnly: this.isTime,
    }

    onChange = (value) => {
        let shouldClose = !(this.isYearView || this.isMonthView);

        switch (this.state.startView) {
            case "years":
                value = this.props.value ? modifyDate(this.props.value, { year: getYear(value) }) : value;
                this.showMonthView();
                break;

            case "months":
                value = this.props.value ? modifyDate(this.props.value, { month: getMonth(value) }) : value;
                this.showDateView();
                break;
        }
        
        this.props.onChange(value, shouldClose);
    }

    getValue() {
        return this.props.value;
    }

    showYearView = () => {
        this.setState({ startView: "years" });
    }

    showMonthView = () => {
        this.setState({ startView: "months" });
    }

    showDateView = () => {
        this.setState({ startView: "dates" });
    }

    get isYearView () {
        return this.state.startView === "years";
    }

    get isMonthView () {
        return this.state.startView === "months";
    }

    renderCustomHeader = (props) => {
        const headerProps = {
            ...props,
            showingMonths: this.isMonthView,
            showingYears: this.isYearView,
            showMonthView: this.showMonthView,
            showYearView: this.showYearView,
        }

        return <CalendarCustomHeader { ...headerProps } />
    }

    render() {
        const { startView, ...state } = this.state;
        const dateTimePickerProps = {
            ...state,
            showYearPicker: this.isYearView,
            showMonthYearPicker: this.isMonthView,
            timeIntervals: 10,
            yearItemNumber: 20,
            calendarStartDay: 1,
            onChange: this.onChange,
            inline: this.props.isInline,
            selected: this.props.value,
            calendarContainer: DatePickerContainer,
            renderCustomHeader: this.renderCustomHeader,
            locale
        }

        return (
            <RDatePicker {...dateTimePickerProps} />
        )
    }
}